import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { livePolling } from '@/app/query-client'
import { pricesKeys } from '@/features/prices/api/keys'
import { pricesService } from '@/features/prices/api/pricesService'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type {
  GainersLosers,
  GainersLosersQuery,
  LiveMarketPage,
  LiveMarketQuery,
  PriceHistory,
  StockPricesPage,
  StockPricesQuery,
  Week52Page,
  Week52Query,
  Week52Row,
} from '@/schemas/prices'
import type { MarketRange } from '@/features/market/api/marketApi'

/**
 * The four price tables.
 *
 * All of them page by offset behind `keepPreviousData`: the pager is a row of
 * numbered buttons, so clicking 3 must not blank the table back to skeleton
 * rows and jump the page under the cursor. The previous page stays painted
 * until the next one lands, and `isPlaceholderData` is the flag a screen's
 * refresh region keys on.
 */

/**
 * Live market — `/market/live`'s MAIN data, keyed on the whole search query
 * (sector, breadth, page, pageSize, sort). The loader awaits it for the
 * ENTRY URL's query; the factory carries the key and the fetch only.
 */
export const liveMarketQueryOptions = (query: LiveMarketQuery) =>
  queryOptions<LiveMarketPage>({
    queryKey: pricesKeys.liveMarket(query),
    queryFn: () => pricesService.liveMarket(query),
  })

/**
 * The one table on a poll: it is the last-session snapshot during a closed
 * market and a moving one during trading hours. The gate is `useMarketStatus`
 * exactly as `useIndexSummary` does it, so an overnight tab issues nothing —
 * a print cannot change while NEPSE is shut.
 */
export const useLiveMarket = (query: LiveMarketQuery): UseQueryResult<LiveMarketPage> => {
  const { isOpen } = useMarketStatus()

  return useQuery<LiveMarketPage>({
    ...liveMarketQueryOptions(query),
    refetchInterval: livePolling(isOpen),
    placeholderData: keepPreviousData,
  })
}

/**
 * Stock prices: the same rows as Live market, searchable and not polled —
 * the page is a reference table, not a tape.
 *
 * The factory is what `/market/stock-prices`' loader awaits for the entry URL
 * (`StockPricesScreen.loader.ts`) — a search-keyed MAIN read, so the screen
 * stays on the plain `useQuery` below rather than suspense (a key change on
 * every keystroke or pager click would otherwise suspend to a full-page
 * skeleton instead of dimming the table in place).
 */
export const stockPricesQueryOptions = (query: StockPricesQuery) =>
  queryOptions<StockPricesPage>({
    queryKey: pricesKeys.stockPrices(query),
    queryFn: ({ client }) => pricesService.stockPrices(query, client),
  })

export const useStockPrices = (query: StockPricesQuery): UseQueryResult<StockPricesPage> =>
  useQuery({
    ...stockPricesQueryOptions(query),
    placeholderData: keepPreviousData,
  })

/**
 * 52-week change, ordered by distance from one end of the yearly range.
 *
 * The factory is `/market/52-week`'s own search-keyed MAIN read — see
 * `stockPricesQueryOptions` above for why the screen keeps the plain
 * `useQuery` rather than moving to suspense.
 */
export const week52QueryOptions = (query: Week52Query) =>
  queryOptions<Week52Page>({
    queryKey: pricesKeys.week52(query),
    queryFn: ({ client }) => pricesService.week52(query, client),
  })

export const useWeek52 = (query: Week52Query): UseQueryResult<Week52Page> =>
  useQuery({
    ...week52QueryOptions(query),
    placeholderData: keepPreviousData,
  })

/**
 * NEPSE as a 52-week row (`pricesService.nepseWeek52`). Its own query, not part of a page of the
 * table: it is one index row from the shared `/market/indices/` entry, pinned on page 1, and a
 * deploy that cannot serve it simply draws the companies without it.
 */
export const week52NepseQueryOptions = () =>
  queryOptions<Week52Row | null>({
    queryKey: pricesKeys.week52Nepse(),
    queryFn: ({ client }) => pricesService.nepseWeek52(client),
  })

export const useWeek52Nepse = (): UseQueryResult<Week52Row | null> =>
  useQuery(week52NepseQueryOptions())

/**
 * Gainers and losers, both columns in one query.
 *
 * They are two readings of the same session and the page prints them side by
 * side, so separate keys would let the left column render one close and the
 * right column the next. The factory is `/market/gainers-losers`'s own
 * search-keyed MAIN read (`period`, `page` and `pageSize`; mm-be serves one period
 * today — see `GAINERS_LOSERS_WEEK_AVAILABLE`) — same
 * plain-`useQuery` reasoning as `stockPricesQueryOptions` above.
 */
export const gainersLosersQueryOptions = (query: GainersLosersQuery) =>
  queryOptions<GainersLosers>({
    queryKey: pricesKeys.gainersLosers(query),
    queryFn: ({ client }) => pricesService.gainersLosers(query, client),
  })

export const useGainersLosers = (query: GainersLosersQuery): UseQueryResult<GainersLosers> =>
  useQuery({
    ...gainersLosersQueryOptions(query),
    placeholderData: keepPreviousData,
  })

/**
 * One session's closes, symbol + range keyed.
 *
 * `keepPreviousData`: the range chip is the only thing that changes this
 * key, so it must not drop the sparkline or the History table to a skeleton
 * on every chip tap.
 *
 * `options.enabled` lets a caller that does not always need this series skip
 * the request rather than issue it unconditionally — see StockScreen.tsx,
 * which only wants the sparkline on the tabs that render one, and
 * PriceHistoryPanel.tsx, which shares this same hook (and, when its range
 * matches StockScreen's, the same cache entry) rather than requesting its
 * own.
 *
 * Stock › Technical and Advanced › Trading read their thirty sessions of
 * volume and turnover off the three-month entry of this same key
 * (`screens/stock/lib/sessions.ts`), so the series has one mapper and one
 * cache entry however many tabs average it. The owning client goes through, as
 * it does for every factory here, so the raw endpoint entry underneath belongs
 * to the client the query lives in and a test's fresh client never reads
 * another's.
 */
export const priceHistoryQueryOptions = (symbol: string, range: MarketRange) =>
  queryOptions<PriceHistory>({
    queryKey: pricesKeys.history(symbol, range),
    queryFn: ({ client }) => pricesService.priceHistory(symbol, range, client),
  })

export const usePriceHistory = (
  symbol: string,
  range: MarketRange,
  options?: { enabled?: boolean },
): UseQueryResult<PriceHistory> =>
  useQuery({
    ...priceHistoryQueryOptions(symbol, range),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  })
